import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { projectService } from '../../../services/projectService';

const AddPhasesModal = ({ isOpen, onClose, projectId, existingPhases = [], onPhasesAdded }) => {
  const [defaultPhases, setDefaultPhases] = useState([]);
  const [selectedPhases, setSelectedPhases] = useState([]);
  const [customPhaseName, setCustomPhaseName] = useState('');
  const [customPhases, setCustomPhases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadDefaultPhases();
      setCustomPhases([]);
      setCustomPhaseName('');
      setError(null);
    }
  }, [isOpen]);

  const loadDefaultPhases = async () => {
    setLoading(true);
    try {
      const { data } = await projectService?.getDefaultPhases();
      const phases = data || [];
      setDefaultPhases(phases);
      // Do NOT pre-select any phases — user must explicitly choose which to add
      setSelectedPhases([]);
    } catch (err) {
      console.error('Error loading default phases:', err?.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleDefaultPhase = (phaseId) => {
    setSelectedPhases((prev) =>
      prev?.includes(phaseId) ? prev?.filter((id) => id !== phaseId) : [...prev, phaseId]
    );
  };

  const handleAddCustomPhase = () => {
    const trimmed = customPhaseName?.trim();
    if (!trimmed) return;
    // Prevent duplicates
    const allNames = [
      ...defaultPhases?.map((p) => p?.name?.toLowerCase()),
      ...customPhases?.map((p) => p?.name?.toLowerCase()),
      ...existingPhases?.map((p) => p?.name?.toLowerCase()),
    ];
    if (allNames?.includes(trimmed?.toLowerCase())) {
      setError(`A phase named "${trimmed}" already exists.`);
      return;
    }
    setCustomPhases((prev) => [...prev, { id: `custom-${Date.now()}`, name: trimmed }]);
    setCustomPhaseName('');
    setError(null);
  };

  const handleRemoveCustomPhase = (id) => {
    setCustomPhases((prev) => prev?.filter((p) => p?.id !== id));
  };

  const handleSave = async () => {
    const phasesToAdd = [
      ...defaultPhases?.filter((dp) => selectedPhases?.includes(dp?.id)),
      ...customPhases,
    ];

    if (phasesToAdd?.length === 0) {
      setError('Please select or add at least one phase.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const sortBase = existingPhases?.length || 0;
      const today = new Date()?.toISOString()?.split('T')?.[0];
      const inserts = phasesToAdd?.map((phase, index) => ({
        projectId,
        name: phase?.name,
        description: phase?.description || '',
        status: 'Planning',
        progress: 0,
        sortOrder: sortBase + index + 1,
        startDate: today,
        endDate: today,
      }));

      const results = await Promise.all(inserts?.map((p) => projectService?.createPhase(p)));
      const failed = results?.filter((r) => r?.error);
      if (failed?.length > 0) {
        setError('Some phases could not be saved. Please try again.');
        return;
      }

      onPhasesAdded?.();
      onClose();
    } catch (err) {
      console.error('Error saving phases:', err?.message);
      setError('Failed to save phases. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const existingNames = existingPhases?.map((p) => p?.name?.toLowerCase()?.trim()) || [];

  return (
    <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent/10 flex-shrink-0">
            <Icon name="GitBranch" size={16} className="text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-foreground">Add Phases</h2>
            <p className="text-xs text-muted-foreground">Select default phases or add custom ones</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              {/* Default Phases */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Default Phases
                </p>
                {defaultPhases?.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No default phases configured.</p>
                ) : (
                  <div className="space-y-1.5">
                    {defaultPhases?.map((phase) => {
                      const alreadyAdded = existingNames?.includes(phase?.name?.toLowerCase()?.trim());
                      const isSelected = selectedPhases?.includes(phase?.id);
                      return (
                        <label
                          key={phase?.id}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                            alreadyAdded
                              ? 'border-border/40 bg-muted/30 opacity-60 cursor-not-allowed'
                              : isSelected
                              ? 'border-accent/60 bg-accent/5' :'border-border hover:border-border/80 hover:bg-muted/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={alreadyAdded}
                            onChange={() => !alreadyAdded && toggleDefaultPhase(phase?.id)}
                            className="rounded border-border text-accent focus:ring-accent"
                          />
                          <span className="text-sm text-foreground flex-1">{phase?.name}</span>
                          {alreadyAdded && (
                            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                              Already added
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Custom Phases */}
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Custom Phases
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customPhaseName}
                    onChange={(e) => { setCustomPhaseName(e?.target?.value); setError(null); }}
                    onKeyDown={(e) => e?.key === 'Enter' && handleAddCustomPhase()}
                    placeholder="Enter phase name..."
                    className="flex-1 text-sm px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent"
                  />
                  <button
                    onClick={handleAddCustomPhase}
                    disabled={!customPhaseName?.trim()}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Icon name="Plus" size={14} />
                    Add
                  </button>
                </div>

                {customPhases?.length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    {customPhases?.map((phase) => (
                      <div
                        key={phase?.id}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg border border-accent/40 bg-accent/5"
                      >
                        <Icon name="Plus" size={13} className="text-accent flex-shrink-0" />
                        <span className="text-sm text-foreground flex-1">{phase?.name}</span>
                        <button
                          onClick={() => handleRemoveCustomPhase(phase?.id)}
                          className="p-0.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                        >
                          <Icon name="X" size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200">
                  <Icon name="AlertCircle" size={14} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs text-red-700">{error}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            {selectedPhases?.length + customPhases?.length} phase{selectedPhases?.length + customPhases?.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleSave}
              disabled={saving || loading || (selectedPhases?.length + customPhases?.length === 0)}
              iconName={saving ? undefined : 'Check'}
              iconPosition="left"
            >
              {saving ? 'Saving...' : 'Add Phases'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPhasesModal;
